import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchDashboardSummary } from "../../store/actions/dashboard";
import WasteTrendChart from "./WasteTrendChart";

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Dobré ráno";
  if (h < 18) return "Dobrý deň";
  return "Dobrý večer";
};

const fmt = (num) =>
  num == null
    ? "—"
    : new Intl.NumberFormat("sk-SK", { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(num);

const KpiCard = ({ label, value, unit, sub, color }) => (
  <div style={{
    background: "#fff",
    borderRadius: 14,
    padding: "20px 24px",
    boxShadow: "0 2px 12px rgba(11,17,87,0.07)",
    flex: 1,
    minWidth: 0,
  }}>
    <p style={{ color: "#718094", fontSize: 13, margin: 0, fontWeight: 500 }}>{label}</p>
    <p style={{ color: color ?? "#0B1157", fontSize: 26, fontWeight: 700, margin: "6px 0 0", lineHeight: 1.1 }}>
      {value}
      {unit && <span style={{ fontSize: 14, fontWeight: 500, color: "#718094", marginLeft: 4 }}>{unit}</span>}
    </p>
    {sub && <p style={{ color: "#a0aec0", fontSize: 12, margin: "4px 0 0" }}>{sub}</p>}
  </div>
);

const ConsentWidget = ({ total, active, expiring, expired }) => {
  if (total === 0) return null;

  const hasIssues = expired > 0 || expiring > 0;
  const borderColor = expired > 0 ? "#dc3545" : expiring > 0 ? "#F0AD4E" : "#e2e8f0";

  return (
    <div style={{
      background: "#fff",
      borderRadius: 14,
      padding: "20px 24px",
      boxShadow: "0 2px 12px rgba(11,17,87,0.07)",
      borderLeft: `4px solid ${borderColor}`,
    }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <p style={{ color: "#718094", fontSize: 13, margin: 0, fontWeight: 500 }}>Súhlasy</p>
        <span style={{ color: "#0B1157", fontSize: 20, fontWeight: 700 }}>
          {total}
          <span style={{ fontSize: 13, fontWeight: 500, color: "#718094", marginLeft: 6 }}>celkom</span>
        </span>
      </div>
      <div className="d-flex gap-3 flex-wrap">
        <span style={{ fontSize: 13, color: "#198754" }}>
          <strong>{active}</strong> aktívnych
        </span>
        {expiring > 0 && (
          <span style={{ fontSize: 13, color: "#C97D2C" }}>
            <strong>{expiring}</strong> vyprší do 4 mes.
          </span>
        )}
        {expired > 0 && (
          <span style={{ fontSize: 13, color: "#dc3545" }}>
            <strong>{expired}</strong> expirovaných
          </span>
        )}
      </div>
      {!hasIssues && (
        <p style={{ color: "#198754", fontSize: 12, margin: "6px 0 0" }}>Všetky súhlasy sú platné</p>
      )}
    </div>
  );
};

const PpwrComplianceWidget = ({ total, complete, incomplete }) => {
  const pct = total > 0 ? Math.round((complete / total) * 100) : 0;
  const barColor = pct === 100 ? "#198754" : pct >= 70 ? "#F0AD4E" : "#dc3545";

  return (
    <div style={{
      background: "#fff",
      borderRadius: 14,
      padding: "20px 24px",
      boxShadow: "0 2px 12px rgba(11,17,87,0.07)",
    }}>
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <p style={{ color: "#718094", fontSize: 13, margin: 0, fontWeight: 500 }}>PPWR — Úplnosť dokumentácie</p>
          <p style={{ color: "#0B1157", fontSize: 20, fontWeight: 700, margin: "4px 0 0" }}>
            {complete} / {total}
            <span style={{ fontSize: 13, fontWeight: 500, color: "#718094", marginLeft: 6 }}>jednotiek</span>
          </p>
        </div>
        <span style={{
          fontSize: 22, fontWeight: 700,
          color: barColor,
          background: barColor + "18",
          borderRadius: 8,
          padding: "4px 12px",
        }}>{pct}%</span>
      </div>
      <div style={{ background: "#f0f2f8", borderRadius: 6, height: 8, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: barColor, borderRadius: 6, transition: "width 0.4s" }} />
      </div>
      {incomplete > 0 && (
        <p style={{ color: "#C97D2C", fontSize: 12, margin: "8px 0 0" }}>
          {incomplete} {incomplete === 1 ? "jednotka má" : incomplete < 5 ? "jednotky majú" : "jednotiek má"} neúplnú dokumentáciu
        </p>
      )}
      {incomplete === 0 && total > 0 && (
        <p style={{ color: "#198754", fontSize: 12, margin: "8px 0 0" }}>Všetky jednotky majú kompletnú dokumentáciu</p>
      )}
    </div>
  );
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const activeTenantId = useSelector((state) => state.users.data.activeTenantId);
  const activeTenantBlocked = useSelector((state) => state.users.data?.activeTenantBlocked);
  const tenantListLoaded = useSelector((state) => state.users.data?.tenantListLoaded);
  const name = useSelector((state) => state.users.data?.name);
  const modules = useSelector((state) => state.users.data?.modules);
  const hasPpwr = modules === "all" || modules === undefined || (Array.isArray(modules) && modules.includes("ppwr"));
  const hasWaste = modules === "all" || modules === undefined || (Array.isArray(modules) && modules.includes("waste"));

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (activeTenantBlocked || !tenantListLoaded || (!hasWaste && !hasPpwr)) { setLoading(false); return; }
    setLoading(true);
    setSummary(null);
    dispatch(fetchDashboardSummary())
      .unwrap()
      .then((data) => setSummary(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dispatch, activeTenantId, tenantListLoaded, activeTenantBlocked, hasWaste, hasPpwr]);

  return (
    <div key={activeTenantId} className="container-fluid py-4 px-0">
      <div className="mb-4">
        <h4 style={{ color: "#0B1157", fontWeight: 700, margin: 0 }}>
          {getGreeting()}{name ? `, ${name}` : ""}
        </h4>
        <p style={{ color: "#718094", fontSize: 14, margin: "4px 0 0" }}>
          Tu je prehľad vašich aktivít za {new Date().getFullYear()}
        </p>
      </div>

      {activeTenantBlocked || !tenantListLoaded ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: 200 }}>
          <p style={{ color: "#718094", fontSize: 16 }}>Subjekt je zablokovaný. Prepnite na iný subjekt v bočnom menu.</p>
        </div>
      ) : (
        <>
          {/* Waste KPI cards — only for waste module users */}
          {hasWaste && (
            <div className="d-flex gap-3 flex-wrap mb-4">
              <KpiCard
                label="Vznik odpadu"
                value={loading ? "…" : fmt(summary?.waste?.ytdIncome)}
                unit="t"
                sub="Celkový príjem od 1. januára"
              />
              <KpiCard
                label="Nakladanie s odpadom"
                value={loading ? "…" : fmt(summary?.waste?.ytdDisposal)}
                unit="t"
                sub="Celkové nakladanie od 1. januára"
              />
              <KpiCard
                label="Zostatok"
                value={loading ? "…" : fmt(summary?.waste?.ytdBalance)}
                unit="t"
                color={
                  summary?.waste?.ytdBalance < 0 ? "#dc3545"
                  : summary?.waste?.ytdBalance > 0 ? "#0B1157"
                  : undefined
                }
                sub="Vznik mínus nakladanie"
              />
              <KpiCard
                label="Evidenčné listy"
                value={loading ? "…" : (summary?.waste?.evidenceListCount ?? "—")}
                sub="Celkový počet evidenčných listov"
              />
            </div>
          )}

          {/* Consents + PPWR widgets */}
          {!loading && (summary?.consents || summary?.ppwr) && (
            <div className="d-flex gap-3 flex-wrap mb-4">
              {hasWaste && summary?.consents?.total > 0 && (
                <div style={{ flex: 1, minWidth: 0 }}>
                  <ConsentWidget {...summary.consents} />
                </div>
              )}
              {hasPpwr && summary?.ppwr?.total > 0 && (
                <div style={{ flex: 1, minWidth: 0 }}>
                  <PpwrComplianceWidget {...summary.ppwr} />
                </div>
              )}
            </div>
          )}

          {/* Main trend chart — only for waste module users */}
          {hasWaste && <WasteTrendChart />}
        </>
      )}
    </div>
  );
};

export default Dashboard;
